import { definedNotNull, wait } from '@df/common';
import { get, writable } from 'svelte/store';
import type { WorkerMessage, WorkerRequest, WorkerResponse } from './types';

function createWorkerStore<ToWorker extends WorkerRequest, FromWorker extends WorkerResponse>(
  onmessage: (this: Worker, ev: WorkerMessage<FromWorker>) => void,
) {
  const store = writable<Worker | null>(null);

  // Add onmessage listener for worker
  store.subscribe((worker) => {
    if (definedNotNull(worker)) {
      worker.onmessage = onmessage;
    }
  });

  async function getWorker(): Promise<Worker> {
    let worker: Worker | null = get(store);
    let backoff = 0;
    while (!definedNotNull(worker)) {
      await wait(backoff);
      worker = get(store);
      backoff += 50;
    }

    return worker;
  }

  async function sendMessage(msg: ToWorker) {
    const worker = await getWorker();

    worker.postMessage(msg);
  }

  const { subscribe, set, update } = store;

  return { subscribe, set, update, sendMessage };
}

function terminateWorkers(...workers: (Worker | null)[]) {
  for (const worker of workers) {
    worker?.terminate();
  }
}

export { createWorkerStore, terminateWorkers };
